/**
 * Base file to handle Axios via different dynamic properties
 * which in turns make this library as a universal solution
 *
 * Author: Poojan Pradhan
 */

import { accessToken } from "./tokenService";
import axiosRetry from "axios-retry";
import axios, { AxiosRequestConfig } from "axios";

import { logError } from "./logService";
import { ReadOnlyProps } from "../../utils/templates";

interface httpProps {
  baseURL?: string;
  type: string;
  path: string;
  body?: any;
  contentType?: string;
  isTokenRequired?: boolean;
  isAcceptHeaderRequired?: boolean;
  timeout?: number;
  responseType?: AxiosRequestConfig["responseType"];
}

export const httpMStepsClient = async ({
  baseURL,
  type,
  path,
  body,
  contentType = "application/json",
  isTokenRequired = true,
  isAcceptHeaderRequired = true,
  timeout = 60000, // Increased default timeout to 60s
  responseType,
}: ReadOnlyProps<httpProps>) => {
  const headers: Record<string, string> = {
    "Content-Type": contentType,
  };

  if (isAcceptHeaderRequired) {
    headers["Accept"] = "application/json";
  }

  if (isTokenRequired) {
    try {
      const tokenData = await accessToken();
      if (tokenData && tokenData.accessToken) {
        headers["Authorization"] = `Bearer ${tokenData.accessToken}`;
      }
    } catch (e) {
      console.warn("Failed to get access token", e);
    }
  }

  // Create a new axios instance to avoid global pollution
  const client = axios.create({
    baseURL,
    headers,
    timeout,
    ...(responseType && { responseType }),
  });

  // Configure retry logic
  axiosRetry(client, {
    retries: 3,
    retryDelay: axiosRetry.exponentialDelay,
    retryCondition: (error) => {
      // Retry on network errors or 5xx status codes
      return (
        axiosRetry.isNetworkOrIdempotentRequestError(error) ||
        (error.response?.status ? error.response.status >= 500 : false)
      );
    },
  });

  //Axios Interceptors
  client.interceptors.response.use(
    (response) => {
      if (response.status === 401) {
        logError("You are not authorized");
      }
      return response;
    },
    (error) => {
      const expectedError =
        error &&
        error.response &&
        error.response.status >= 400 &&
        error.response.status < 500;

      if (!expectedError) {
        logError(
          error?.response?.data?.message ||
            error?.message ||
            error ||
            "An error occurred",
        );
      }

      return Promise.reject(
        error?.response?.data?.message ||
          error?.message ||
          error ||
          "An error occurred",
      );
    },
  );

  let axiosMethod: any = null;
  switch (type) {
    case "GET":
      axiosMethod = client.get(path);
      break;
    case "POST":
      axiosMethod = client.post(path, body);
      break;
    case "PUT":
      axiosMethod = client.put(path, body);
      break;
    case "PATCH":
      axiosMethod = client.patch(path, body);
      break;
    case "DELETE":
      axiosMethod = client.delete(path);
      break;
  }

  return axiosMethod;
};
